import WasmController from "react-lib/frameworks/WasmController";

import PPHDWithMaxReimbursementList from "issara-sdk/apis/PPHDWithMaxReimbursementList_apps_PRX";

import { State as MainState } from "HIS/MainHISInterface";
import { SetProperty } from "react-lib/apps/HISV3/common/CommonInterface";
import moment from "moment";

export type State = Partial<{
  // sequence
  WellnessEMSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    filterWellnessEM: FilterWellnessEMType;
    wellnessPPHDList: WellnessPPHDListType;
  }> | null;
}>;

type FilterWellnessEMType = {
  name?: string;
  hn?: string;
  citizen_passport?: string;
  user_id?: number | string;
  is_verified?: boolean | string;
};

type WellnessPPHDListType = {
  items?: Record<string, any>[];
  activePage?: number;
  total?: number;
  editItems?: number[];
};

type Picked = Partial<
  Pick<MainState, "buttonLoadCheck" | "errorMessage" | "django" | "searchedItemListWithKey">
>;

// Sequence
type SeqState = {
  sequence: "WellnessEmployeeManagement";
  restart?: boolean;
  clear?: boolean;
  card?: string;
  filterByDivision?: boolean;
  activePage?: number;
};

// Handle Action
type ActionType =
  // Search
  | { action: "SEARCH"; card?: string; activePage?: number }
  | { action: "SAVE"; card?: string; onCallBack?: () => {} };

type SeqAct = ActionType & SeqState;
type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => any;

export type SetProp = SetProperty<State & Picked>;

type CustomExtract<T, U> = T extends T ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const DEFAULT_LIST_LIMIT = 10;

export const StateInitial: State = {
  // sequence
  WellnessEMSequence: {
    sequenceIndex: null,
  },
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
  device?: number;
};

export const BUTTON_ACTIONS = {
  SEARCH: "SEARCH",
  SAVE: "SAVE",
} as const;

type Handler<P = any, R = any> = (
  controller: WasmController<State & Picked, Event, Data, Props>,
  params: P,
) => R;

/* ------------------------------------------------------ */

/*                          START                         */

/* ------------------------------------------------------ */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      WellnessEMSequence: {
        ...state.WellnessEMSequence,
        sequenceIndex: "Action",
        filterWellnessEM: state.WellnessEMSequence?.filterWellnessEM ?? {
          name: "",
          hn: "",
          citizen_passport: "",
          user_id: "",
          is_verified: "ALL",
        },
      },
    },
    () =>
      Action(controller, {
        action: "SEARCH",
        ...params,
      }),
  );
};

/* ------------------------------------------------------ */

/*                      Handle Action                     */

/* ------------------------------------------------------ */
export const Action: Handler<ActionType> = async (controller, params) => {
  if (params.action === "SEARCH") {
    HandleSearch(controller, params);
  } else if (params.action === "SAVE") {
    HandleSave(controller, params);
  }
};

const HandleSearch: Handler<Params<"SEARCH">> = async (controller, params) => {
  const state = controller.getState();

  const activePage = Math.max(1, params.activePage ?? 1);
  const offset = (activePage - 1) * DEFAULT_LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.card}_${params.action}`]: "LOADING",
    },
  });

  const filter = state.WellnessEMSequence?.filterWellnessEM;

  const data = {
    limit: DEFAULT_LIST_LIMIT,
    offset,
    name: filter?.name,
    hn: filter?.hn,
    citizen_passport: filter?.citizen_passport,
    user_id: filter?.user_id,
    ...(filter?.is_verified !== "ALL" &&
      filter?.is_verified !== undefined && {
        is_verified: filter.is_verified,
      }),
  };

  const [pphdRes, pphdErr] = await PPHDWithMaxReimbursementList.list({
    params: data,
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (pphdRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}`]: "SUCCESS",
      },
      WellnessEMSequence: {
        ...state.WellnessEMSequence,
        wellnessPPHDList: {
          ...state.WellnessEMSequence?.wellnessPPHDList,
          activePage,
          items: pphdRes.items || [],
          total: pphdRes.total || 0,
          editItems: [],
        },
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.card}_${params.action}`]: "ERROR",
      },
    });
  }
};

const HandleSave: Handler<Params<"SAVE">> = async (controller, params) => {
  const state = controller.getState();
  const cardKey = params.card ?? "";
  const actionKey = `${params.card}_${params.action}`;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [actionKey]: "LOADING",
    },
  });

  // ข้อมูลที่กำลังจะบันทึก
  const editItems = state.WellnessEMSequence?.wellnessPPHDList?.editItems || [];

  // ตรวจสอบข้อมูลก่อนบันทึก
  const validation = validatePatientCoverage(editItems);

  if (!validation.isValid) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [actionKey]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [cardKey]: validation.errorMessage.join("\n"),
      },
    });

    return;
  }

  // ผ่าน validation แล้วจึงเรียก API
  const [pphdRes, pphdErr] = await PPHDWithMaxReimbursementList.create({
    data: {
      items: editItems,
    },
    apiToken: controller.apiToken,
    extra: { division: controller.data.division },
  });

  if (pphdRes) {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [actionKey]: "SUCCESS",
      },
      errorMessage: {
        ...controller.getState().errorMessage,
        [cardKey]: "",
      },
    });

    HandleSearch(controller, {
      action: "SEARCH",
      activePage: 1,
    });

    params.onCallBack?.();
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...controller.getState().buttonLoadCheck,
        [actionKey]: "ERROR",
      },
      errorMessage: {
        ...controller.getState().errorMessage,
        [cardKey]: pphdErr,
      },
    });
  }
};

const validatePatientCoverage = (items: any[]) => {
  const errors: {
    index1: number;
    index2: number;
    field: string;
    message: string;
    name: string;
  }[] = [];

  const parseDate = (value: any) => {
    if (!value) return null;

    const date = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY"], true);

    return date.isValid() ? date.startOf("day") : null;
  };

  items.forEach((item: any, index1: number) => {
    const coverages = item.patient_coverage_max_reimbs || [];

    // 1. ตรวจสอบแต่ละแถว
    coverages.forEach((coverage: any, index2: number) => {
      const startDate = parseDate(coverage.start_date);
      const stopDate = parseDate(coverage.stop_date);
      const usedAmount = Number(coverage.used_amount ?? 0);
      const maxReimb = Number(coverage.max_reimb);

      // วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด
      if (startDate && stopDate && startDate.isAfter(stopDate)) {
        errors.push({
          index1,
          index2,
          field: "start_date",
          message: "วันที่เริ่มต้นต้องไม่มากกว่าวันที่สิ้นสุด",
          name: item?.name,
        });
      }

      // วงเงินต้องไม่ติดลบ
      if (
        coverage.max_reimb !== "" &&
        coverage.max_reimb != null &&
        Number.isFinite(maxReimb) &&
        maxReimb < 0
      ) {
        errors.push({
          index1,
          index2,
          field: "max_reimb",
          message: "วงเงินต้องไม่น้อยกว่า 0",
          name: item?.name,
        });
      }

      // ห้ามปรับลดวงเงินต่ำกว่ายอดที่ใช้ไปแล้ว
      if (
        Number.isFinite(usedAmount) &&
        Number.isFinite(maxReimb) &&
        usedAmount > 0 &&
        maxReimb < usedAmount
      ) {
        errors.push({
          index1,
          index2,
          field: "max_reimb",
          message: `วงเงินต้องไม่น้อยกว่ายอดที่ใช้ไปแล้ว (${usedAmount.toFixed(2)})`,
          name: item?.name,
        });
      }
    });

    // 2. ตรวจสอบช่วงวันที่ทับซ้อนกัน เฉพาะ active: true
    const activeCoverages = coverages
      .map((coverage: any, index2: number) => ({
        ...coverage,
        index2,
        parsedStart: parseDate(coverage.start_date),
        parsedStop: parseDate(coverage.stop_date),
      }))
      .filter(
        (coverage: any) =>
          coverage.active === true &&
          coverage.parsedStart &&
          coverage.parsedStop &&
          !coverage.parsedStart.isAfter(coverage.parsedStop),
      );

    for (let i = 0; i < activeCoverages.length; i++) {
      for (let j = i + 1; j < activeCoverages.length; j++) {
        const a = activeCoverages[i];
        const b = activeCoverages[j];

        // ช่วงวันที่ซ้อนทับกัน รวมวันที่ตรงกันด้วย
        const isOverlapping =
          a.parsedStart.isSameOrBefore(b.parsedStop) && b.parsedStart.isSameOrBefore(a.parsedStop);

        if (isOverlapping) {
          errors.push({
            index1,
            index2: b.index2,
            field: "start_date",
            message: `ช่วงวันที่ทับซ้อนกับแถวที่ ${a.index2 + 1}`,
            name: item?.name,
          });
        }
      }
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    errorMessage: errors
      .map((error) => `พนักงาน ${error.name} รายการที่ ${error.index2 + 1}: ${error.message}`)
      .filter((message, index, array) => array.indexOf(message) === index),
  };
};
